import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import SinConexionKawaii from "../SinConexionKawaii";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template63: Página de Shibuya (mapa, cruce, Hachiko) - Día 2 página 3
// Copia EXACTA de Page12.jsx de guia-tokio - contenido fijo, no editable
const Template63 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const [cruceImageError, setCruceImageError] = useState(false);
	const [shibuyaImageError, setShibuyaImageError] = useState(false);
	const [fotomatonImageError, setFotomatonImageError] = useState(false);

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	const mapUrl = page.maps?.[0];
	const contentColor = textColor || "#444";

	// Vista fullscreen del mapa
	if (fullscreen) {
		return (
			<div
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<div
					style={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#24221a",
					}}
				>
					<button
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						style={{
							backgroundColor: "#fff",
							color: "#24221a",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: fontFamily || "'Poppins', sans-serif",
						}}
					>
						← Volver a la guía
					</button>
				</div>
				{isOnline && mapUrl ? (
					<iframe
						title="Mapa grande"
						src={mapUrl}
						width="100%"
						height="100%"
						style={{ border: 0, flex: 1 }}
						allowFullScreen
						loading="lazy"
					/>
				) : (
					<div
						style={{
							flex: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<SinConexionKawaii
							height="100%"
							fontFamily={fontFamily || "'Poppins', sans-serif"}
						/>
					</div>
				)}
			</div>
		);
	}

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const headerText = page.texts?.[0]?.content;
	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};
	const cruceImage = getImageSrc("cruce");
	const shibuyaImage = getImageSrc("shibuya");
	const fotomatonImage = getCanvasImageUrl("fotomaton");
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;
	const cruceTitle = page.texts?.[1]?.content;
	const cruceText = page.texts?.[2]?.content;
	const masEnShibuyaTitle = page.texts?.[3]?.content;
	const masEnShibuyaText = page.texts?.[4]?.content;
	const sabiasQueCruce = page.texts?.[5]?.content;
	const sabiasQueHachiko = page.texts?.[6]?.content;
	const purikuraStickerText = page.texts?.[7]?.content;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{headerText}
				<img
					src={logoImage}
					alt="logo"
					style={{
						width: isSmallScreen ? 66 : 80,
						position: "absolute",
						right: 2,
						top: 18,
						zIndex: 999999,
					}}
				/>
			</Box>

			{/* MAPA - altura fija para que el iframe del mapa se vea bien */}
			<Box
				data-block-id="map"
				sx={{
					position: "relative",
					backgroundColor: "#f7f7f7",
					marginTop: "-60px",
					height: 480,
					minHeight: 480,
				}}
			>
				<Box sx={{ flex: 1, marginTop: "-70px" }}>
					{!loaded && isOnline && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								color: "black",
								fontFamily: "'Poppins', sans-serif",
								fontSize: 14,
								zIndex: 1,
							}}
						>
							<Box
								sx={{
									width: 20,
									height: 20,
									border: "4px solid #ccc",
									borderTop: "4px solid #24221a",
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
								}}
							/>
						</Box>
					)}

					{isOnline && (
						<button
							onClick={() => {
								setFullscreen(true);
								onMapFullscreenChange?.(true);
							}}
							style={{
								position: "absolute",
								top: 70,
								right: 10,
								backgroundColor: "#24221a",
								color: "#fff",
								padding: "8px 10px",
								fontSize: "11px",
								borderRadius: "8px",
								textDecoration: "none",
								fontFamily: "'Poppins', sans-serif",
								boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

								border: "none",
								cursor: "pointer",
							}}
						>
							Ver mapa grande
						</button>
					)}

					{isOnline && mapUrl ? (
						<iframe
							title="Mapa de Shibuya"
							src={mapUrl}
							width="100%"
							height="550px"
							allowFullScreen
							loading="lazy"
							style={{ border: 0 }}
							onLoad={() => setLoaded(true)}
						/>
					) : !isOnline ? (
						<SinConexionKawaii height="550px" fontFamily={fontFamily} />
					) : null}
				</Box>

				{fotomatonImage && !fotomatonImageError && (
					<img
						src={fotomatonImage}
						alt=""
						onError={() => setFotomatonImageError(true)}
						style={{
							width: 120,
							position: "absolute",
							bottom: -20,
							right: -20,
						}}
					/>
				)}
				<Box
					sx={{
						position: "absolute",
						bottom: isSmallScreen ? 80 : 60,
						right: isSmallScreen ? 6 : 40,
						backgroundColor: "#fff8db",
						border: "2px solid #f5c400",
						borderRadius: "16px 16px 0px 16px",
						padding: "10px 12px",
						fontSize: 11,
						fontFamily: "'Poppins', sans-serif",
						color: contentColor,
						boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
						maxWidth: 120,
					}}
				>
					{purikuraStickerText}
				</Box>
			</Box>

			{/* EXPLICACIÓN ABAJO */}
			<Box sx={{ padding: "10px 12px", position: "relative" }}>
				{/* Cruce de Shibuya */}
				<Box
					data-block-id="text-1"
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						gap: 2,
						position: "relative",
					}}
				>
					<Box
						sx={{
							display: "flex",
							gap: "20px",
							alignItems: "flex-start",
						}}
					>
						{cruceImage && !cruceImageError ? (
							<img
								src={cruceImage}
								alt=""
								onError={() => setCruceImageError(true)}
								style={{
									width: "40%",
									height: 110,
									borderRadius: 12,
									objectFit: "cover",
								}}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width="40%"
								height={110}
								fontFamily={fontFamily}
								message="Imagen no disponible"
								compact
							/>
						)}

						<Box sx={{ position: "absolute", top: 0, left: "30%" }}>
							<SabiasQue icono="🎬" texto={sabiasQueCruce} />
						</Box>

						<Box sx={{ width: "60%" }}>
							<Box
								component="h3"
								sx={{
									fontFamily: "'Poppins', sans-serif",
									marginBottom: "4px",
									fontSize: 14,
									fontWeight: 700,
									textTransform: "uppercase",
									marginTop: "8px",
								}}
							>
								{cruceTitle}
							</Box>
							<Box
								component="p"
								sx={{
									fontSize: 13,
									color: contentColor,
									fontFamily: "'Poppins', sans-serif",
									margin: 0,
								}}
							>
								{cruceText}
							</Box>
						</Box>
					</Box>
				</Box>

				{/* MÁS EN SHIBUYA */}
				<Box
					data-block-id="text-2"
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						gap: 2,
						position: "relative",
						marginTop: 2,
					}}
				>
					<Box
						sx={{
							display: "flex",
							gap: "20px",
							alignItems: "flex-start",
						}}
					>
						{shibuyaImage && !shibuyaImageError ? (
							<img
								src={shibuyaImage}
								alt=""
								onError={() => setShibuyaImageError(true)}
								style={{
									width: "40%",
									height: 110,
									borderRadius: 12,
									objectFit: "cover",
								}}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width="40%"
								height={110}
								fontFamily={fontFamily}
								message="Imagen no disponible"
								compact
							/>
						)}

						<Box sx={{ width: "60%" }}>
							<Box
								component="h3"
								sx={{
									fontFamily: "'Poppins', sans-serif",
									marginBottom: "4px",
									fontSize: 14,
									fontWeight: 700,
									textTransform: "uppercase",
									marginTop: "8px",
								}}
							>
								{masEnShibuyaTitle}
							</Box>
							<Box
								component="p"
								sx={{
									fontSize: 13,
									color: contentColor,
									fontFamily: "'Poppins', sans-serif",
									margin: 0,
								}}
							>
								{masEnShibuyaText}
							</Box>
						</Box>

						<Box sx={{ position: "absolute", bottom: 10, left: "30%" }}>
							<SabiasQue icono="🐕" texto={sabiasQueHachiko} position="above" />
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template63.thumbnail = "/images/template-1.png";
export default Template63;
